import React, { useState, useContext, useReducer } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import SuperTokens from 'supertokens-auth-react';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleQuestion,
  faArrowRightFromBracket,
  faArrowRightToBracket,
  faMagnifyingGlass,
  faMoneyBillTransfer,
  faUser,
  faChevronRight as falChevronRight,
  faChevronLeft as falChevronLeft,
} from '@fortawesome/pro-light-svg-icons';
import {
  faChevronLeft,
  faChevronRight,
  faBars,
  faLock,
  faAngleDown,
  faAngleUp,
  faDownload,
  faTriangleExclamation,
  faOctagonXmark,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faArrowLeftLong,
  faArrowRightLong,
  faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import { faHouseBlank } from '@fortawesome/pro-solid-svg-icons';

import entities, { entitiesState } from './reducers/entities';
import AccountIndex from './components/pages/entity_detail/AccountIndex';
import CompanyIndex from './components/pages/entity_detail/CompanyIndex';
import Home from './components/pages/home/Index';
import OrderIndex from './components/pages/order_detail/OrderIndex';
import Login from './components/pages/Login';
import LoginTokenForm from './components/pages/LoginToken';
import PortalLayout from './components/layout/PortalLayout';
import SearchConfig from './components/shared/SearchConfig';
import { useHydrateAuth, useSuperTokensAuth } from './hooks/useAuth';

import SuperTokensConfig from './lib/constants/SuperTokensConfig';
import { USE_SUPERTOKENS } from './lib/constants/SiteVariables';
import Loading from './components/shared/Loading';

export const ApiContext = React.createContext();
export const AuthContext = React.createContext();
export const EntitiesContext = React.createContext();
export const UserContext = React.createContext();
export const QueryContext = React.createContext();

const queryClient = new QueryClient();

library.add(
  faArrowRightFromBracket,
  faArrowRightToBracket,
  faBars,
  faCircleQuestion,
  faChevronLeft,
  faChevronRight,
  faHouseBlank,
  faLock,
  faUser,
  faAngleDown,
  faArrowLeftLong,
  faArrowRightLong,
  faMoneyBillTransfer,
  faAngleUp,
  faMagnifyingGlass,
  faDownload,
  falChevronRight,
  falChevronLeft,
  faCircleXmark,
  faTriangleExclamation,
  faOctagonXmark,
);

const headers = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

if (USE_SUPERTOKENS) SuperTokens.init(SuperTokensConfig);

function App() {
  const [auth, setAuth] = useState(null);
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState();
  const [state, dispatch] = useReducer(entities, entitiesState);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthContext.Provider value={{ auth, setAuth }}>
        <ApiContext.Provider
          value={axios.create({
            baseURL: process.env.REACT_APP_API,
            headers: headers,
          })}
        >
          <EntitiesContext.Provider value={{ state, dispatch }}>
            <UserContext.Provider value={{ user, setUser }}>
              <QueryContext.Provider value={{ searchQuery, setSearchQuery }}>
                <BrowserRouter>
                  <PortalLayout>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <RequireAuth>
                            <SearchConfig>
                              <Home />
                            </SearchConfig>
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/companies/:id"
                        element={
                          <RequireAuth>
                            <SearchConfig>
                              <CompanyIndex />
                            </SearchConfig>
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/accounts/:id"
                        element={
                          <RequireAuth>
                            <SearchConfig>
                              <AccountIndex />
                            </SearchConfig>
                          </RequireAuth>
                        }
                      />
                      <Route
                        path="/orders/:id"
                        element={
                          <RequireAuth>
                            <SearchConfig>
                              <OrderIndex />
                            </SearchConfig>
                          </RequireAuth>
                        }
                      />
                      <Route path="/login" element={<Login />} />
                      <Route
                        path="/login-token/:type/:contact"
                        element={<LoginTokenForm />}
                      />
                    </Routes>
                  </PortalLayout>
                </BrowserRouter>
              </QueryContext.Provider>
            </UserContext.Provider>
          </EntitiesContext.Provider>
        </ApiContext.Provider>
      </AuthContext.Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

function RequireAuth({ children }) {
  return USE_SUPERTOKENS ? (
    <WithSuperTokensAuth>{children}</WithSuperTokensAuth>
  ) : (
    <WithHydrateAuth>{children}</WithHydrateAuth>
  );
}

function WithSuperTokensAuth({ children }) {
  const auth = useSuperTokensAuth();
  return <ProvideAuth auth={auth}>{children}</ProvideAuth>;
}

function WithHydrateAuth({ children }) {
  const auth = useHydrateAuth();
  return <ProvideAuth auth={auth}>{children}</ProvideAuth>;
}

function ProvideAuth({ auth, children }) {
  const navigate = useNavigate();
  const api = useContext(ApiContext);

  if (auth.isLoading()) {
    return <Loading className="w-14 h-14" />;
  }

  if (!auth.isAuthenticated()) {
    return auth.redirectToLogin();
  }

  api.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      if (error?.response?.status === 403) navigate('/');

      return Promise.reject(error);
    },
  );

  api.defaults.headers.common['Authorization'] = `Bearer ${auth.accessToken()}`;

  return children;
}

export default App;
